import { usePageRefresh } from '../../../../context/PageRefreshContext';
import { useState, useEffect, useCallback } from 'react';
import { Icon } from '@iconify/react';
import { useDispatch, useSelector } from 'react-redux';
import { getCDNoteReport } from '../../../../redux/actions/SocietyPortal/InvoiceReportAction/InvoiceReportAction';
import { getInvoiceTypes } from '../../../../redux/actions/SocietyPortal/InvoiceTypeAction/InvoiceTypeAction';
import { getWingsDropdown, getFlatsDropdown } from '../../../../redux/actions/WingsAndFlatsActions/WingsAndFlatsAction';
import { exportCDNoteReportReq } from '../../../../axios/request/SocietyPortal/InvoiceReportReqs/InvoiceReportReqs';
import { selectedSocietyStorer } from '../../../../utils/webStorer';
import { PaginationLimit } from '../../../../components/PaginationLimit';

const NOTE_TYPE_OPTIONS = [
    { label: 'All', value: 'ALL' },
    { label: 'Credit Note', value: 'CREDIT_NOTE' },
    { label: 'Debit Note', value: 'DEBIT_NOTE' },
];

const STATUS_OPTIONS = [
    { label: 'All', value: 'ALL' },
    { label: 'Posted', value: 'POSTED' },
    { label: 'Draft', value: 'DRAFT' },
    { label: 'Utilized', value: 'UTILIZED' },
    { label: 'Void', value: 'VOID' },
];

const STATUS_STYLES = {
    POSTED: 'tw-bg-blue-50 tw-text-blue-700',
    DRAFT: 'tw-bg-gray-100 tw-text-gray-500',
    PAID: 'tw-bg-emerald-50 tw-text-emerald-700',
    PARTIAL: 'tw-bg-amber-50 tw-text-amber-700',
    UTILIZED: 'tw-bg-purple-50 tw-text-purple-700',
    VOID: 'tw-bg-red-50 tw-text-red-700',
    CANCELLED: 'tw-bg-gray-200 tw-text-gray-600',
};

const CDNoteReport = () => {
    const dispatch = useDispatch();
    const societyId = selectedSocietyStorer.getSocietyId();

    const { cdNoteReportData, cdNoteReportSummary, cdNoteReportPagination, cdNoteReportLoading } = useSelector(state => state.InvoiceReportReducer || {});
    const { invoiceTypesData } = useSelector(state => state.InvoiceTypeReducers || {});
    const { wingsDropdown, flatsDropdown } = useSelector(state => state.WingsAndFlatsReducers || {});

    const [filters, setFilters] = useState({
        note_type: 'ALL', invoice_type_id: '', wing_id: '', flat_id: '',
        from_date: '', to_date: '', status_filter: 'ALL', search: '',
    });
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [expandedRows, setExpandedRows] = useState({});
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        dispatch(getInvoiceTypes({ society_id: societyId, page: 1, limit: 100 }));
        dispatch(getWingsDropdown({ society_id: societyId }));
    }, [dispatch, societyId]);

    useEffect(() => {
        if (filters.wing_id) dispatch(getFlatsDropdown({ wing_id: filters.wing_id }));
    }, [dispatch, filters.wing_id]);

    const fetchReport = useCallback(() => {
        const params = { society_id: societyId, page, limit };
        if (filters.note_type !== 'ALL') params.note_type = filters.note_type;
        if (filters.invoice_type_id) params.invoice_type_id = filters.invoice_type_id;
        if (filters.wing_id) params.wing_id = filters.wing_id;
        if (filters.flat_id) params.flat_id = filters.flat_id;
        if (filters.from_date) params.from_date = filters.from_date;
        if (filters.to_date) params.to_date = filters.to_date;
        if (filters.status_filter !== 'ALL') params.status_filter = filters.status_filter;
        if (filters.search) params.search = filters.search;
        dispatch(getCDNoteReport(params));
    }, [dispatch, societyId, filters, page, limit]);
    usePageRefresh(fetchReport);

    useEffect(() => { fetchReport(); }, [fetchReport]);

    const handleFilterChange = (name, value) => {
        setFilters(prev => {
            const updated = { ...prev, [name]: value };
            if (name === 'wing_id') updated.flat_id = '';
            return updated;
        });
        setPage(1);
    };

    const toggleRow = (noteId) => {
        setExpandedRows(prev => ({ ...prev, [noteId]: !prev[noteId] }));
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            const params = { society_id: societyId };
            if (filters.note_type !== 'ALL') params.note_type = filters.note_type;
            if (filters.invoice_type_id) params.invoice_type_id = filters.invoice_type_id;
            if (filters.wing_id) params.wing_id = filters.wing_id;
            if (filters.flat_id) params.flat_id = filters.flat_id;
            if (filters.from_date) params.from_date = filters.from_date;
            if (filters.to_date) params.to_date = filters.to_date;
            if (filters.status_filter !== 'ALL') params.status_filter = filters.status_filter;
            if (filters.search) params.search = filters.search;
            const response = await exportCDNoteReportReq(params);
            const blob = new Blob([response], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'credit_debit_note_report.xlsx';
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            console.error('Export failed:', e);
        } finally {
            setExporting(false);
        }
    };

    const handleReset = () => {
        setFilters({ note_type: 'ALL', invoice_type_id: '', wing_id: '', flat_id: '', from_date: '', to_date: '', status_filter: 'ALL', search: '' });
        setPage(1);
        setExpandedRows({});
    };

    const notes = cdNoteReportData || [];
    const summary = cdNoteReportSummary || {};
    const pagination = cdNoteReportPagination || {};
    const wings = wingsDropdown || [];
    const flats = Array.isArray(flatsDropdown) ? flatsDropdown : flatsDropdown?.data || [];
    const invoiceTypes = invoiceTypesData || [];

    const formatCurrency = (amount) => parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        const d = new Date(dateStr);
        return d.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    return (
        <>
            {/* Header */}
            <div className="tw-flex tw-items-start tw-justify-between tw-mb-4">
                <div>
                    <h1 className="tw-text-lg tw-font-bold tw-text-gray-800">Credit / Debit Note Report</h1>
                    <p className="tw-text-xs tw-text-gray-400 tw-mt-0.5">View credit and debit notes</p>
                </div>
                <button onClick={handleExport} disabled={exporting || cdNoteReportLoading}
                    className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-gray-600 tw-border tw-border-gray-200 tw-rounded-lg hover:tw-bg-gray-50 tw-transition-colors disabled:tw-opacity-50">
                    {exporting ? <Icon icon="mdi:loading" className="tw-text-sm tw-animate-spin" /> : <Icon icon="mdi:file-excel-outline" className="tw-text-sm" />}
                    {exporting ? 'Exporting...' : 'Export Excel'}
                </button>
            </div>

            {/* Summary Cards */}
            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-3 tw-mb-4">
                <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-4">
                    <div className="tw-text-[11px] tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wide">Total Notes</div>
                    <div className="tw-text-xl tw-font-bold tw-text-gray-800 tw-mt-1">{summary.total_notes || 0}</div>
                </div>
                <div className="tw-bg-white tw-rounded-lg tw-border tw-border-emerald-200 tw-p-4">
                    <div className="tw-flex tw-items-center tw-justify-between">
                        <div>
                            <div className="tw-text-[11px] tw-font-medium tw-text-emerald-600 tw-uppercase tw-tracking-wide">Credit Notes</div>
                            <div className="tw-text-xl tw-font-bold tw-text-emerald-700 tw-mt-1">{formatCurrency(summary.credit_notes_total)}</div>
                        </div>
                        <span className="tw-text-xs tw-text-gray-400">{summary.credit_notes_count || 0} notes</span>
                    </div>
                </div>
                <div className="tw-bg-white tw-rounded-lg tw-border tw-border-red-200 tw-p-4">
                    <div className="tw-flex tw-items-center tw-justify-between">
                        <div>
                            <div className="tw-text-[11px] tw-font-medium tw-text-red-600 tw-uppercase tw-tracking-wide">Debit Notes</div>
                            <div className="tw-text-xl tw-font-bold tw-text-red-700 tw-mt-1">{formatCurrency(summary.debit_notes_total)}</div>
                        </div>
                        <span className="tw-text-xs tw-text-gray-400">{summary.debit_notes_count || 0} notes</span>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-4 tw-mb-4">
                <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-4 lg:tw-grid-cols-8 tw-gap-3">
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">Note Type</label>
                        <select value={filters.note_type} onChange={(e) => handleFilterChange('note_type', e.target.value)}
                            className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500">
                            {NOTE_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">Invoice Type</label>
                        <select value={filters.invoice_type_id} onChange={(e) => handleFilterChange('invoice_type_id', e.target.value)}
                            className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500">
                            <option value="">All Types</option>
                            {invoiceTypes.map(t => <option key={t.invoice_type_id} value={t.invoice_type_id}>{t.invoice_title}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">Wing</label>
                        <select value={filters.wing_id} onChange={(e) => handleFilterChange('wing_id', e.target.value)}
                            className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500">
                            <option value="">All Wings</option>
                            {wings.map(w => <option key={w.wing_id} value={w.wing_id}>{w.title}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">Flat</label>
                        <select value={filters.flat_id} onChange={(e) => handleFilterChange('flat_id', e.target.value)} disabled={!filters.wing_id}
                            className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500 disabled:tw-bg-gray-50 disabled:tw-text-gray-400">
                            <option value="">All Flats</option>
                            {flats.map(f => <option key={f.flat_id} value={f.flat_id}>{f.flat_number || f.flat_name}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">From Date</label>
                        <input type="date" value={filters.from_date} onChange={(e) => handleFilterChange('from_date', e.target.value)}
                            className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500" />
                    </div>
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">To Date</label>
                        <input type="date" value={filters.to_date} onChange={(e) => handleFilterChange('to_date', e.target.value)}
                            className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500" />
                    </div>
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">Status</label>
                        <select value={filters.status_filter} onChange={(e) => handleFilterChange('status_filter', e.target.value)}
                            className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500">
                            {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">Search</label>
                        <div className="tw-relative">
                            <Icon icon="mdi:magnify" className="tw-absolute tw-left-2.5 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400 tw-text-sm" />
                            <input type="text" placeholder="Doc no or member..." value={filters.search} onChange={(e) => handleFilterChange('search', e.target.value)}
                                className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-pl-8 tw-pr-3 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500" />
                        </div>
                    </div>
                </div>
                <div className="tw-flex tw-justify-end tw-mt-3">
                    <button onClick={handleReset} className="tw-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-gray-500 tw-border tw-border-gray-200 tw-rounded-md hover:tw-bg-gray-50">
                        <Icon icon="mdi:refresh" className="tw-text-sm" /> Reset
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-overflow-hidden">
                <div className="tw-overflow-x-auto">
                    <table className="tw-w-full tw-text-xs">
                        <thead>
                            <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-200">
                                <th className="tw-px-2 tw-py-2.5 tw-w-8"></th>
                                {['Sr No', 'Type', 'Document No', 'Issue Date', 'Wing', 'Unit', 'Member Name', 'Invoice Type', 'Against Invoice', 'Total', 'Tax', 'Net Amount', 'Paid', 'Balance', 'Reason', 'Status'].map((h, i) => (
                                    <th key={i} className="tw-px-3 tw-py-2.5 tw-text-[11px] tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wider tw-whitespace-nowrap"
                                        style={{ textAlign: [0, 3, 15].includes(i) ? 'center' : [9, 10, 11, 12, 13].includes(i) ? 'right' : 'left' }}>
                                        {h}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {cdNoteReportLoading ? (
                                <tr><td colSpan={17} className="tw-text-center tw-py-12">
                                    <Icon icon="mdi:loading" className="tw-animate-spin tw-text-2xl tw-text-gray-300 tw-mx-auto" />
                                    <p className="tw-text-xs tw-text-gray-400 tw-mt-2">Loading report...</p>
                                </td></tr>
                            ) : notes.length === 0 ? (
                                <tr><td colSpan={17} className="tw-text-center tw-py-12">
                                    <Icon icon="mdi:file-document-outline" className="tw-text-4xl tw-text-gray-200 tw-mx-auto" />
                                    <p className="tw-text-xs tw-text-gray-400 tw-mt-2">No credit/debit notes found</p>
                                </td></tr>
                            ) : notes.map((note, idx) => {
                                const isExpanded = expandedRows[note.note_id];
                                const items = note.items || [];
                                const isCN = note.note_type === 'CREDIT_NOTE';
                                return (
                                    <>
                                        <tr key={note.note_id} onClick={() => items.length > 0 && toggleRow(note.note_id)}
                                            className={`tw-border-b tw-border-gray-100 ${items.length > 0 ? 'tw-cursor-pointer hover:tw-bg-gray-50/50' : ''} ${isExpanded ? 'tw-bg-blue-50/30' : ''}`}>
                                            <td className="tw-px-2 tw-py-2 tw-text-center">
                                                {items.length > 0 && <Icon icon={isExpanded ? 'mdi:chevron-down' : 'mdi:chevron-right'} className="tw-text-gray-400 tw-text-base" />}
                                            </td>
                                            <td className="tw-px-3 tw-py-2 tw-text-center tw-text-gray-500">{(page - 1) * limit + idx + 1}</td>
                                            <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap">
                                                <span className={`tw-inline-flex tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-medium ${isCN ? 'tw-bg-emerald-50 tw-text-emerald-700' : 'tw-bg-red-50 tw-text-red-700'}`}>
                                                    {note.note_type_label}
                                                </span>
                                            </td>
                                            <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap tw-text-gray-700 tw-font-medium">{note.document_number}</td>
                                            <td className="tw-px-3 tw-py-2 tw-text-center tw-whitespace-nowrap tw-text-gray-700">{formatDate(note.issue_date)}</td>
                                            <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap tw-text-gray-700">{note.wing}</td>
                                            <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap tw-text-gray-700">{note.unit_no}</td>
                                            <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap tw-text-gray-700">{note.member_name}</td>
                                            <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap tw-text-gray-500">{note.invoice_type}</td>
                                            <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap tw-text-gray-500">{note.invoice_no || '-'}</td>
                                            <td className="tw-px-3 tw-py-2 tw-text-right tw-whitespace-nowrap tw-text-gray-700">{formatCurrency(note.total_amount)}</td>
                                            <td className="tw-px-3 tw-py-2 tw-text-right tw-whitespace-nowrap tw-text-gray-600">{formatCurrency(note.tax_amount)}</td>
                                            <td className="tw-px-3 tw-py-2 tw-text-right tw-whitespace-nowrap tw-font-medium tw-text-gray-800">{formatCurrency(note.net_amount)}</td>
                                            <td className="tw-px-3 tw-py-2 tw-text-right tw-whitespace-nowrap tw-text-gray-600">{formatCurrency(note.amount_paid)}</td>
                                            <td className="tw-px-3 tw-py-2 tw-text-right tw-whitespace-nowrap tw-font-medium tw-text-gray-800">{formatCurrency(note.balance_due)}</td>
                                            <td className="tw-px-3 tw-py-2 tw-text-gray-500 tw-max-w-[150px] tw-truncate" title={note.reason}>{note.reason || '-'}</td>
                                            <td className="tw-px-3 tw-py-2 tw-text-center tw-whitespace-nowrap">
                                                <span className={`tw-inline-flex tw-px-2 tw-py-0.5 tw-rounded tw-text-[11px] tw-font-medium ${STATUS_STYLES[note.status] || 'tw-bg-gray-50 tw-text-gray-600'}`}>
                                                    {note.status}
                                                </span>
                                            </td>
                                        </tr>
                                        {isExpanded && items.map((item, i) => (
                                            <tr key={`${note.note_id}-item-${i}`} className="tw-bg-gray-50/70 tw-border-b tw-border-gray-100">
                                                <td colSpan={2}></td>
                                                <td colSpan={15} className="tw-px-3 tw-py-2">
                                                    <div className="tw-flex tw-items-center tw-gap-6 tw-text-[11px]">
                                                        <div>
                                                            <span className="tw-text-gray-400">Head:</span>{' '}
                                                            <span className="tw-text-gray-700 tw-font-medium">{item.head_name}</span>
                                                        </div>
                                                        <div>
                                                            <span className="tw-text-gray-400">Base:</span>{' '}
                                                            <span className="tw-text-gray-700 tw-font-medium">{formatCurrency(item.base_amount)}</span>
                                                        </div>
                                                        <div>
                                                            <span className="tw-text-gray-400">Tax:</span>{' '}
                                                            <span className="tw-text-gray-700">{formatCurrency(item.tax_amount)}</span>
                                                        </div>
                                                        <div>
                                                            <span className="tw-text-gray-400">Total:</span>{' '}
                                                            <span className={`tw-font-bold ${isCN ? 'tw-text-emerald-600' : 'tw-text-red-600'}`}>{formatCurrency(item.total_amount)}</span>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {pagination.total > 0 && (
                <PaginationLimit
                    currentPage={page}
                    totalRecords={pagination.total || 0}
                    limit={limit}
                    handleLimitChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
                    handlePageChange={(p) => setPage(p)}
                />
            )}
        </>
    );
};

export default CDNoteReport;
